/**
 * Wire shapes for `RouteOptimizationJobController` (`api/v1/core/route-optimization/jobs`).
 * Mirrors the backend records field-for-field — convert to module shapes before use.
 */

export const ROUTE_OPTIMIZATION_JOB_STATUS = {
  QUEUED: 'QUEUED',
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  TIMED_OUT: 'TIMED_OUT',
} as const

export type RouteOptimizationJobStatus = typeof ROUTE_OPTIMIZATION_JOB_STATUS[keyof typeof ROUTE_OPTIMIZATION_JOB_STATUS]

/** Mirrors `RouteOptimizationJobStatus.NON_TERMINAL`; a status outside it is never polled again. */
export const NON_TERMINAL_JOB_STATUSES: ReadonlySet<RouteOptimizationJobStatus> = new Set([
  ROUTE_OPTIMIZATION_JOB_STATUS.QUEUED,
  ROUTE_OPTIMIZATION_JOB_STATUS.PENDING,
  ROUTE_OPTIMIZATION_JOB_STATUS.RUNNING,
])

/** Mirrors the submit's `@Pattern(regexp = "time|distance")`. */
export type OptimizeForApi = 'time' | 'distance'

/** Mirrors `RouteOptimizationAvoidance`, which the submit parses case-insensitively. */
export type AvoidanceApi = 'tolls' | 'highways' | 'uturns' | 'left_turns' | 'right_turns' | 'sharp_turns' | 'service_roads'

export interface YardApi {
  latitude: number
  longitude: number
}

export interface OptimizationStopApi {
  stopId: string
  latitude: number
  longitude: number
}

/** Millimetres and kilograms. `axleCount` is rejected outright, so it has no field here. */
export interface VehicleProfileApi {
  heightMm?: number
  widthMm?: number
  lengthMm?: number
  weightKg?: number
  hazardousGoods?: string[]
}

export const ROUTE_OPTIMIZATION_SCOPE = {
  MASTER: 'MASTER',
  DAILY: 'DAILY',
} as const

export type RouteOptimizationScopeApi = typeof ROUTE_OPTIMIZATION_SCOPE[keyof typeof ROUTE_OPTIMIZATION_SCOPE]

/** The dispatcher's verdict on a finished run. Absent means they have yet to decide. */
export const ROUTE_OPTIMIZATION_RESOLUTION = {
  APPLIED: 'APPLIED',
  REJECTED: 'REJECTED',
} as const

export type RouteOptimizationResolutionApi = typeof ROUTE_OPTIMIZATION_RESOLUTION[keyof typeof ROUTE_OPTIMIZATION_RESOLUTION]

export interface CreateRouteOptimizationJobBody {
  routeId: string
  serviceDate: string
  /** Required: a master and a daily run of the same route and date are separate jobs. */
  scope: RouteOptimizationScopeApi
  startingYard: YardApi
  endingYard: YardApi
  approach?: string
  vehicleProfile?: VehicleProfileApi
  /** `time` or `distance`; omit for the vendor's default. Any other value is a 400. */
  optimizeFor?: OptimizeForApi
  /** Soft avoidances. Omit rather than sending an empty list. */
  avoid?: AvoidanceApi[]
  stops: OptimizationStopApi[]
}

/** Null fields are omitted by the backend, so everything optional here is genuinely absent. */
export interface RouteOptimizationJobApi {
  jobId: string
  routeId?: string
  serviceDate?: string
  scope?: RouteOptimizationScopeApi
  status: RouteOptimizationJobStatus
  failureReason: string | null
  resolution?: RouteOptimizationResolutionApi
  resolvedAt?: string
  createdAt: string
  updatedAt: string
}

export interface ResolveRouteOptimizationJobBody {
  resolution: RouteOptimizationResolutionApi
}

export interface OptimizedStopApi extends OptimizationStopApi {
  sequence: number
}

export interface SkippedStopApi extends OptimizationStopApi {
  reasonCode: string | null
}

export interface RouteOptimizationJobResultApi {
  jobId: string
  status: RouteOptimizationJobStatus
  optimizedStops: OptimizedStopApi[]
  skippedStops: SkippedStopApi[]
}
